import React, { useState, useCallback, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';

import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import app from './firebase';

import Navbar from './components/Navbar';
import Toast from './components/Toast';
import GlobalLoader from './components/GlobalLoader';
import LandingPage from './components/LandingPage';
import Footer from './components/Footer';
import posthog from 'posthog-js';

// Lazy loaded page components for code splitting
const AuthPage = lazy(() => import('./components/AuthPage'));

const StudentPage = lazy(() => import('./components/StudentPage'));
const CompaniesPage = lazy(() => import('./components/CompaniesPage'));
const PostJobPage = lazy(() => import('./components/PostJobPage'));
const ProfilePage = lazy(() => import('./components/ProfilePage'));
const RecruiterDashboard = lazy(() => import('./components/RecruiterDashboard'));
const ResumeBuilder = lazy(() => import('./components/ResumeBuilder'));
const MessagesPage = lazy(() => import('./components/MessagesPage'));
const StudentSetupPage = lazy(() => import('./components/StudentSetupPage'));
const RecruiterSetupPage = lazy(() => import('./components/RecruiterSetupPage'));
const JobDetailsPage = lazy(() => import('./components/JobDetailsPage'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));
const VerifyEmailPage = lazy(() => import('./components/VerifyEmailPage'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));

const auth = getAuth(app);
const db = getFirestore(app);

// Wrapper to enforce setup flows
function RequireSetup({ user, authLoading, children }) {
  const location = useLocation();

  if (authLoading) {
    return <GlobalLoader />;
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (!user.emailVerified && location.pathname !== '/verify-email') {
    return <Navigate to="/verify-email" replace />;
  }

  if (user.role === 'student' && !user.hasResume && location.pathname !== '/setup') {
    return <Navigate to="/setup" replace />;
  }

  if (user.role === 'recruiter' && !user.setupComplete && location.pathname !== '/recruiter-setup') {
    return <Navigate to="/recruiter-setup" replace />;
  }

  return children;
}

// Wrapper for Admin routes
function RequireAdmin({ user, authLoading, children }) {
  if (authLoading) {
    return <GlobalLoader />;
  }
  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  return children;
}

export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [toast, setToast] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const hideFooterRoutes = ['/admin', '/dashboard', '/messages', '/resume', '/setup', '/recruiter-setup', '/auth', '/verify-email'];
  const shouldHideFooter = hideFooterRoutes.some(route => location.pathname.startsWith(route));

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Force token refresh to get latest Custom Claims
            const idTokenResult = await firebaseUser.getIdTokenResult(true);
            const claims = idTokenResult.claims;
            const tokenRole = claims.admin ? 'admin' : (claims.recruiter ? 'recruiter' : (claims.student ? 'student' : userData.role));

            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              emailVerified: firebaseUser.emailVerified,
              role: tokenRole || userData.role,
              name: userData.name,
              hasResume: userData.hasResume || false,
              setupComplete: userData.setupComplete || false,
              approvalStatus: userData.approvalStatus || 'pending',
              status: userData.status || 'active',
              skills: userData.skills || [],
              college: userData.college || '',
              degree: userData.degree || '',
              organizationName: userData.organizationName || '',
              designation: userData.designation || '',
              industry: userData.industry || ''
            });

            posthog.identify(firebaseUser.uid, {
              email: firebaseUser.email,
              role: tokenRole || userData.role,
              hasResume: userData.hasResume || false,
              setupComplete: userData.setupComplete || false
            });
          } else {
            setUser(null);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const showToast = useCallback((msg) => setToast(msg), []);
  const clearToast = useCallback(() => setToast(''), []);

  const handleApply = useCallback((job) => {
    if (!user) {
      navigate('/auth');
      showToast('Please login to apply.');
      return;
    }
    if (user.role === 'recruiter') {
      showToast('Recruiters cannot apply for jobs. Please log in as a student.');
      return;
    }
    showToast(`Applied to ${job.title} at ${job.company}!`);
  }, [showToast, user, navigate]);

  const handlePostSubmit = useCallback(() => {
    showToast('Opportunity posted successfully!');
    setTimeout(() => navigate('/jobs'), 1200);
  }, [showToast, navigate]);

  const handleAuthSuccess = useCallback((userData) => {
    setUser(userData);

    if (!userData.emailVerified) {
      navigate('/verify-email');
      return;
    }

    if (userData.role === 'admin') {
      navigate('/admin');
      return;
    }

    if (userData.role === 'recruiter') {
      if (!userData.setupComplete) {
        navigate('/recruiter-setup');
      } else {
        navigate('/dashboard');
      }
    } else {
      if (!userData.hasResume) {
        navigate('/setup');
      } else {
        navigate('/');
      }
    }
  }, [navigate]);

  const handleLogout = useCallback(() => {
    auth.signOut();
    setUser(null);
    posthog.reset();
    navigate('/');
  }, [navigate]);

  return (
    <>
      <Navbar user={user} onLogout={handleLogout} />

      <Suspense fallback={<GlobalLoader />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth" element={
            !authLoading && user && user.emailVerified ? <Navigate to="/" replace /> : <AuthPage onAuthSuccess={handleAuthSuccess} />
          } />
          
          <Route path="/verify-email" element={
             !authLoading && user && user.emailVerified ? <Navigate to="/" replace /> : <VerifyEmailPage user={user} />
          } />

          <Route path="/setup" element={<RequireSetup user={user} authLoading={authLoading}><StudentSetupPage user={user} onComplete={(u) => { setUser(u); navigate('/jobs'); }} /></RequireSetup>} />
          <Route path="/recruiter-setup" element={<RequireSetup user={user} authLoading={authLoading}><RecruiterSetupPage user={user} onComplete={(u) => { setUser(u); navigate('/dashboard'); }} /></RequireSetup>} />
          
          <Route path="/companies" element={<CompaniesPage />} />
          
          {/* Protected Routes (Require Onboarding) */}
          <Route path="/jobs" element={<RequireSetup user={user} authLoading={authLoading}><StudentPage onApply={handleApply} user={user} /></RequireSetup>} />
          <Route path="/jobs/delhi" element={<RequireSetup user={user} authLoading={authLoading}><StudentPage onApply={handleApply} user={user} presetLocation="delhi" /></RequireSetup>} />
          <Route path="/jobs/mumbai" element={<RequireSetup user={user} authLoading={authLoading}><StudentPage onApply={handleApply} user={user} presetLocation="mumbai" /></RequireSetup>} />
          <Route path="/jobs/remote" element={<RequireSetup user={user} authLoading={authLoading}><StudentPage onApply={handleApply} user={user} presetLocation="remote" /></RequireSetup>} />
          <Route path="/jobs/marketing" element={<RequireSetup user={user} authLoading={authLoading}><StudentPage onApply={handleApply} user={user} presetCategory="marketing" /></RequireSetup>} />
          <Route path="/jobs/location/:locationParam" element={<RequireSetup user={user} authLoading={authLoading}><StudentPage onApply={handleApply} user={user} /></RequireSetup>} />
          <Route path="/jobs/category/:categoryParam" element={<RequireSetup user={user} authLoading={authLoading}><StudentPage onApply={handleApply} user={user} /></RequireSetup>} />
          <Route path="/jobs/:id" element={<RequireSetup user={user} authLoading={authLoading}><JobDetailsPage onApply={handleApply} user={user} /></RequireSetup>} />
          
          <Route path="/post-job" element={<RequireSetup user={user} authLoading={authLoading}><PostJobPage onSubmit={handlePostSubmit} user={user} /></RequireSetup>} />
          <Route path="/dashboard" element={<RequireSetup user={user} authLoading={authLoading}><RecruiterDashboard user={user} /></RequireSetup>} />
          <Route path="/resume" element={<RequireSetup user={user} authLoading={authLoading}><ResumeBuilder user={user} /></RequireSetup>} />
          <Route path="/profile" element={<RequireSetup user={user} authLoading={authLoading}><ProfilePage user={user} /></RequireSetup>} />
          <Route path="/messages" element={<RequireSetup user={user} authLoading={authLoading}><MessagesPage user={user} /></RequireSetup>} />

          {/* Admin Routes */}
          <Route path="/admin/*" element={<RequireAdmin user={user} authLoading={authLoading}><AdminPanel user={user} /></RequireAdmin>} />

          {/* Catch-all 404 Route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>

      {!shouldHideFooter && <Footer />}

      <Toast message={toast} onClose={clearToast} />
    </>
  );
}

